import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { getApiBaseUrl } from "../../utils/utils";

export default function MedicalAttendanceSetting() {
  const API_URL = process.env.REACT_APP_API_URL;
  const [msg, setMsg] = useState("");
  const [errormsg, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { config, isEdit } = location.state || {};

  // Partners
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(
    config?.partnerId || ""
  );

  // Shifts
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(config?.shiftId || "");
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [newShift, setNewShift] = useState({
    name: "",
    startTime: "",
    endTime: "",
  });

  // Configuration settings
  const [settings, setSettings] = useState({
    selfieRequired: config?.selfieRequired ?? true,
    geofenceRequired: config?.geofenceRequired ?? false,
    active: config?.active ?? true,
  });

  // Geofences for this partner
  const [geofences, setGeofences] = useState([]);
  const [selectedGeofences, setSelectedGeofences] = useState([]);
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [newGeofence, setNewGeofence] = useState({
    name: "",
    latitude: "",
    longitude: "",
    radiusMeters: 100,
    address: "",
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      fetchShifts(selectedPartner);
      fetchGeofences(selectedPartner);
    }
  }, [selectedPartner]);

  const fetchPartners = async () => {
    try {
      const baseApiUrl = getApiBaseUrl();
      const response = await axios.get(
        `${baseApiUrl}/v1.0/partner/retrieve_list?page=0`
      );
      // Get all partners - can be filtered for medical-related if needed
      const allPartners = response.data.data || [];
      // Show all partners or filter by medical-related account types
      const medicalPartners = allPartners.filter(
        (partner) => {
          const accountType = partner.accountType?.toLowerCase() || '';
          // Include medical, lab, hospital, healthcare related or all partners
          return accountType.includes('medical') || 
                 accountType.includes('lab') || 
                 accountType.includes('hospital') ||
                 accountType.includes('health') ||
                 accountType === '' ||
                 true; // Set to true to show all partners
        }
      );
      setPartners(medicalPartners);
      console.log("Fetched partners for medical attendance:", medicalPartners.length, "out of", allPartners.length);
    } catch (err) {
      console.error("Error fetching partners:", err);
      setError("Failed to load partners. Please refresh the page.");
    }
  };

  const fetchShifts = async (partnerId) => {
    if (!partnerId) return;
    try {
      const response = await axios.get(
        `${API_URL}shypvdriverapp/api/medical-attendance/shifts?partnerId=${partnerId}`
      );
      if (response.data.success) {
        setShifts(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching shifts:", err);
      setShifts([]);
    }
  };

  const fetchGeofences = async (partnerId) => {
    try {
      const response = await axios.get(
        `${API_URL}shypvdriverapp/api/medical-attendance/geofences/partner/${partnerId}`
      );
      if (response.data.success) {
        const geoData = response.data.data || [];
        setGeofences(geoData);
        // Pre-select existing geofences if editing
        if (config?.geofenceIds) {
          setSelectedGeofences(config.geofenceIds);
        }
      }
    } catch (err) {
      console.error("Error fetching geofences:", err);
      setGeofences([]);
    }
  };

  const handleCreateShift = async () => {
    if (!selectedPartner) {
      setError("Please select a partner first");
      return;
    }
    if (!newShift.name || !newShift.startTime || !newShift.endTime) {
      setError("Please fill all shift fields");
      return;
    }
    try {
      const payload = {
        ...newShift,
        partnerId: parseInt(selectedPartner),
      };
      const response = await axios.post(
        `${API_URL}shypvdriverapp/api/medical-attendance/shifts`,
        payload
      );
      if (response.data.success) {
        setMsg("Shift created successfully");
        fetchShifts(selectedPartner);
        setShowShiftModal(false);
        setNewShift({ name: "", startTime: "", endTime: "" });
        // Auto-select the new shift
        if (response.data.data?.id) {
          setSelectedShift(response.data.data.id);
        }
      } else {
        setError(response.data.message || "Failed to create shift");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error creating shift");
    }
  };

  const handleCreateGeofence = async () => {
    if (
      !newGeofence.name ||
      !newGeofence.latitude ||
      !newGeofence.longitude ||
      !selectedPartner
    ) {
      setError("Please fill all location fields");
      return;
    }
    try {
      const payload = {
        ...newGeofence,
        partnerId: parseInt(selectedPartner),
        latitude: parseFloat(newGeofence.latitude),
        longitude: parseFloat(newGeofence.longitude),
        radiusMeters: parseInt(newGeofence.radiusMeters) || 100,
      };
      const response = await axios.post(
        `${API_URL}shypvdriverapp/api/medical-attendance/geofences`,
        payload
      );
      if (response.data.success) {
        setMsg("Location added successfully");
        fetchGeofences(selectedPartner);
        setShowGeofenceModal(false);
        setNewGeofence({
          name: "",
          latitude: "",
          longitude: "",
          radiusMeters: 100,
          address: "",
        });
        // Auto-select the new geofence
        if (response.data.data?.id) {
          setSelectedGeofences((prev) => [...prev, response.data.data.id]);
        }
      } else {
        setError(response.data.message || "Failed to add location");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error adding location");
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setNewGeofence((prev) => ({
            ...prev,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6),
          }));
          setMsg("Location captured successfully");
        },
        (error) => {
          setError("Failed to get current location: " + error.message);
        }
      );
    } else {
      setError("Geolocation is not supported by this browser");
    }
  };

  const handleSubmit = async () => {
    setError("");
    setMsg("");

    if (!selectedPartner) {
      setError("Please select a partner");
      return;
    }

    setLoading(true);
    try {
      // Build the payload matching backend MedicalAttendanceConfigDTO structure
      const payload = {
        partnerId: parseInt(selectedPartner),
        driverIds: [], // No drivers by default - drivers will be assigned later
        selfieConfig: {
          enabled: settings.selfieRequired,
          requiredForCheckIn: settings.selfieRequired,
          requiredForCheckOut: settings.selfieRequired,
        },
        locationConfig: {
          enabled: settings.geofenceRequired,
          required: settings.geofenceRequired,
          allowPunchAnywhere: !settings.geofenceRequired,
          defaultRadiusMeters: 100,
        },
      };

      const url = `${API_URL}shypvdriverapp/api/medical-attendance/config`;
      const response = await axios.post(url, payload);

      if (response.data.success) {
        setMsg(
          isEdit
            ? "Configuration updated successfully"
            : "Configuration created successfully"
        );
        setTimeout(() => {
          navigate("/orders/medical-attendance-config");
        }, 1500);
      } else {
        setError(response.data.message || "Failed to save configuration");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error saving configuration");
    } finally {
      setLoading(false);
    }
  };

  const geofenceOptions = geofences.map((g) => ({
    value: g.id,
    label: `${g.name} (${g.radiusMeters}m radius)`,
  }));

  const shiftOptions = shifts.map((s) => ({
    value: s.id,
    label: `${s.name} (${s.startTime} - ${s.endTime})`,
  }));

  const partnerOptions = partners.map((p) => ({
    value: p.pid,
    label: p.pname,
  }));

  // Modern styles matching NormalTaskCreation
  const styles = {
    container: {
      padding: "24px",
      backgroundColor: "#f5f7fa",
      minHeight: "100vh",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      flexWrap: "wrap",
      gap: "12px",
    },
    title: {
      fontSize: "22px",
      fontWeight: "600",
      color: "#2d3748",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    card: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      marginBottom: "20px",
      border: "1px solid #e8ecf1",
    },
    cardHeader: {
      padding: "16px 20px",
      borderBottom: "1px solid #e8ecf1",
      backgroundColor: "#fafbfc",
      borderRadius: "12px 12px 0 0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "10px",
    },
    cardTitle: {
      fontSize: "15px",
      fontWeight: "600",
      color: "#374151",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    cardBody: {
      padding: "20px",
    },
    formRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "20px",
    },
    formGroup: {
      marginBottom: "0",
    },
    label: {
      display: "block",
      fontSize: "13px",
      fontWeight: "500",
      color: "#4b5563",
      marginBottom: "8px",
    },
    input: {
      width: "100%",
      padding: "10px 14px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      transition: "border-color 0.2s",
      outline: "none",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "10px 14px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      cursor: "pointer",
      outline: "none",
    },
    btnPrimary: {
      padding: "12px 24px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#10b981",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "background-color 0.2s",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    btnSecondary: {
      padding: "10px 18px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#4b5563",
      backgroundColor: "#f3f4f6",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    btnSmall: {
      padding: "8px 16px",
      fontSize: "13px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#3b82f6",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    alert: {
      padding: "14px 18px",
      borderRadius: "10px",
      marginBottom: "20px",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    alertError: {
      backgroundColor: "#fef2f2",
      color: "#dc2626",
      border: "1px solid #fecaca",
    },
    alertSuccess: {
      backgroundColor: "#f0fdf4",
      color: "#16a34a",
      border: "1px solid #bbf7d0",
    },
    toggleContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    toggleItem: (isActive) => ({
      display: "flex",
      alignItems: "center",
      gap: "14px",
      padding: "14px 18px",
      borderRadius: "10px",
      backgroundColor: isActive ? "#ecfdf5" : "#f9fafb",
      border: `1px solid ${isActive ? "#10b981" : "#e5e7eb"}`,
      cursor: "pointer",
      transition: "all 0.2s ease",
    }),
    toggleSwitch: (isActive) => ({
      width: "44px",
      height: "24px",
      borderRadius: "12px",
      backgroundColor: isActive ? "#10b981" : "#d1d5db",
      position: "relative",
      transition: "background-color 0.2s",
      flexShrink: 0,
    }),
    toggleKnob: (isActive) => ({
      width: "18px",
      height: "18px",
      borderRadius: "50%",
      backgroundColor: "#fff",
      position: "absolute",
      top: "3px",
      left: isActive ? "23px" : "3px",
      transition: "left 0.2s",
      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    }),
    statsCard: {
      backgroundColor: "#fff",
      borderRadius: "10px",
      padding: "16px",
      textAlign: "center",
      border: "1px solid #e8ecf1",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
  };

  // Legacy style helpers for backwards compatibility
  const inputStyle = styles.input;
  const labelStyle = styles.label;
  const cardStyle = styles.card;
  const toggleStyle = styles.toggleItem;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h5 style={styles.title}>
          <i className="fa fa-medkit" style={{ color: "#10b981" }}></i>
          {isEdit ? "Edit" : "Create"} Medical Attendance Configuration
        </h5>
        <button
          onClick={() => navigate("/orders/medical-attendance-config")}
          style={styles.btnSecondary}
        >
          <i className="fa fa-arrow-left"></i>
          Back to List
        </button>
      </div>

        {msg && (
          <div style={{ ...styles.alert, ...styles.alertSuccess }}>
            <i className="fa fa-check-circle"></i>
            {msg}
          </div>
        )}

        {errormsg && (
          <div style={{ ...styles.alert, ...styles.alertError }}>
            <i className="fa fa-exclamation-circle"></i>
            {errormsg}
          </div>
        )}

        {/* Partner Statistics */}
        {partners.length > 0 && (
          <div style={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
            <div style={styles.statsCard}>
              <div style={{ 
                width: "40px", height: "40px", borderRadius: "10px", 
                backgroundColor: "#ecfdf5", display: "flex", alignItems: "center", 
                justifyContent: "center" 
              }}>
                <i className="fa fa-building" style={{ color: "#10b981", fontSize: "18px" }}></i>
              </div>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#1f2937" }}>{partners.length}</div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>Medical Partners Available</div>
              </div>
            </div>
          </div>
        )}

        {/* Partner Selection Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h6 style={styles.cardTitle}>
              <i className="fa fa-building" style={{ color: "#3b82f6" }}></i>
              Partner Selection
            </h6>
          </div>
          <div style={styles.cardBody}>
            <div>
              <label style={styles.label}>Select Medical Partner *</label>
              <Select
                options={partnerOptions}
                value={partnerOptions.find((p) => p.value === selectedPartner)}
                onChange={(option) => setSelectedPartner(option?.value || "")}
                placeholder={partners.length === 0 ? "Loading partners..." : "Choose a medical partner..."}
                isDisabled={isEdit || partners.length === 0}
                noOptionsMessage={() => partners.length === 0 ? "No medical partners found" : "No options"}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderRadius: "8px",
                    minHeight: "44px",
                    fontSize: "14px",
                    borderColor: state.isFocused ? "#10b981" : "#d1d5db",
                    boxShadow: state.isFocused ? "0 0 0 2px rgba(16, 185, 129, 0.2)" : "none",
                    "&:hover": { borderColor: "#10b981" }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? "#10b981" : state.isFocused ? "#ecfdf5" : "white",
                    color: state.isSelected ? "white" : "#374151",
                  }),
                }}
              />
              {partners.length === 0 && (
                <p style={{ fontSize: "12px", color: "#ef4444", marginTop: "8px" }}>
                  <i className="fa fa-exclamation-triangle" style={{ marginRight: "6px" }}></i>
                  No medical partners found. Please ensure partners with "medical" account type exist.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Shift Selection Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h6 style={styles.cardTitle}>
              <i className="fa fa-clock-o" style={{ color: "#f59e0b" }}></i>
              Shift Configuration
            </h6>
            <button
              onClick={() => setShowShiftModal(true)}
              disabled={!selectedPartner}
              style={{
                ...styles.btnSmall,
                backgroundColor: selectedPartner ? "#3b82f6" : "#9ca3af",
                cursor: selectedPartner ? "pointer" : "not-allowed",
              }}
            >
              <i className="fa fa-plus"></i>
              Add Shift
            </button>
          </div>
          <div style={styles.cardBody}>
            <label style={styles.label}>Select Shift</label>
            <Select
              options={shiftOptions}
              value={shiftOptions.find((s) => s.value === selectedShift)}
              onChange={(option) => setSelectedShift(option?.value || "")}
              placeholder={selectedPartner ? "Choose a shift..." : "Select a partner first"}
              isDisabled={!selectedPartner}
              noOptionsMessage={() => "No shifts available. Create one!"}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderRadius: "8px",
                  minHeight: "44px",
                  fontSize: "14px",
                  borderColor: state.isFocused ? "#f59e0b" : "#d1d5db",
                  boxShadow: state.isFocused ? "0 0 0 2px rgba(245, 158, 11, 0.2)" : "none",
                  "&:hover": { borderColor: "#f59e0b" }
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? "#f59e0b" : state.isFocused ? "#fef3c7" : "white",
                  color: state.isSelected ? "white" : "#374151",
                }),
              }}
            />
          </div>
        </div>

        {/* Verification Settings Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h6 style={styles.cardTitle}>
              <i className="fa fa-shield" style={{ color: "#8b5cf6" }}></i>
              Verification Settings
            </h6>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.toggleContainer}>
              {/* Selfie Toggle */}
              <div
                style={styles.toggleItem(settings.selfieRequired)}
                onClick={() =>
                  setSettings({
                    ...settings,
                    selfieRequired: !settings.selfieRequired,
                  })
                }
              >
                <div style={styles.toggleSwitch(settings.selfieRequired)}>
                  <div style={styles.toggleKnob(settings.selfieRequired)}></div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "500", color: "#1f2937", fontSize: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fa fa-camera" style={{ color: "#8b5cf6" }}></i>
                    Selfie Required
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                    Drivers must capture selfie during check-in/out
                  </div>
                </div>
              </div>

              {/* Geofence Toggle */}
              <div
                style={styles.toggleItem(settings.geofenceRequired)}
                onClick={() =>
                  setSettings({
                    ...settings,
                    geofenceRequired: !settings.geofenceRequired,
                  })
                }
              >
                <div style={styles.toggleSwitch(settings.geofenceRequired)}>
                  <div style={styles.toggleKnob(settings.geofenceRequired)}></div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "500", color: "#1f2937", fontSize: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fa fa-map-marker" style={{ color: "#ef4444" }}></i>
                    Geofence Required
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                    Drivers must be within designated location radius
                  </div>
                </div>
              </div>

              {/* Active Toggle */}
              <div
                style={styles.toggleItem(settings.active)}
                onClick={() =>
                  setSettings({ ...settings, active: !settings.active })
                }
              >
                <div style={styles.toggleSwitch(settings.active)}>
                  <div style={styles.toggleKnob(settings.active)}></div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "500", color: "#1f2937", fontSize: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fa fa-power-off" style={{ color: settings.active ? "#10b981" : "#9ca3af" }}></i>
                    Configuration Active
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                    Enable or disable this attendance configuration
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Geofence/Location Management */}
        {settings.geofenceRequired && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h6 style={styles.cardTitle}>
                <i className="fa fa-map-marker" style={{ color: "#ef4444" }}></i>
                Allowed Locations
              </h6>
              <button
                onClick={() => setShowGeofenceModal(true)}
                disabled={!selectedPartner}
                style={{
                  ...styles.btnSmall,
                  backgroundColor: selectedPartner ? "#10b981" : "#9ca3af",
                  cursor: selectedPartner ? "pointer" : "not-allowed",
                }}
              >
                <i className="fa fa-plus"></i>
                Add Location
              </button>
            </div>
            <div style={styles.cardBody}>
              {!selectedPartner ? (
                <div
                style={{
                  padding: "20px",
                  textAlign: "center",
                  backgroundColor: "#f1f5f9",
                  borderRadius: "12px",
                  border: "1px dashed #cbd5e1",
                }}
              >
                <i
                  className="fa fa-info-circle"
                  style={{ fontSize: "28px", color: "#3b82f6", marginBottom: "12px", display: "block" }}
                ></i>
                <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
                  Please select a partner first to manage locations
                </p>
              </div>
              ) : geofences.length === 0 ? (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  backgroundColor: "#fef3c7",
                  borderRadius: "12px",
                  border: "1px solid #fcd34d",
                }}
              >
                <i
                  className="fa fa-map-marker"
                  style={{ fontSize: "36px", color: "#d97706", marginBottom: "12px", display: "block" }}
                ></i>
                <h6 style={{ color: "#92400e", marginBottom: "8px", fontSize: "16px", fontWeight: "600" }}>
                  No Locations Added Yet
                </h6>
                <p style={{ color: "#a16207", marginBottom: "16px", fontSize: "14px" }}>
                  Add locations where drivers can check in/out
                </p>
                <button
                  onClick={() => setShowGeofenceModal(true)}
                  style={{
                    ...styles.btnPrimary,
                    margin: "0 auto",
                  }}
                >
                  <i className="fa fa-plus"></i>
                  Add First Location
                </button>
              </div>
              ) : (
              <div>
                <div style={{ marginBottom: "16px" }}>
                  <label style={styles.label}>Select Allowed Locations *</label>
                  <Select
                    isMulti
                    options={geofenceOptions}
                    value={geofenceOptions.filter((g) =>
                      selectedGeofences.includes(g.value)
                    )}
                    onChange={(options) =>
                      setSelectedGeofences(
                        options ? options.map((o) => o.value) : []
                      )
                    }
                    placeholder="Select locations..."
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: "8px",
                        minHeight: "44px",
                        borderColor: state.isFocused ? "#ef4444" : "#d1d5db",
                        boxShadow: state.isFocused ? "0 0 0 2px rgba(239, 68, 68, 0.2)" : "none",
                        "&:hover": { borderColor: "#ef4444" }
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? "#ef4444" : state.isFocused ? "#fef2f2" : "white",
                        color: state.isSelected ? "white" : "#374151",
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#ecfdf5",
                        borderRadius: "6px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#065f46",
                        fontWeight: "500",
                      }),
                    }}
                  />
                </div>

                {/* Display selected locations */}
                {selectedGeofences.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                      marginTop: "12px",
                    }}
                  >
                    {geofences
                      .filter((g) => selectedGeofences.includes(g.id))
                      .map((geo) => (
                        <div
                          key={geo.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "10px 14px",
                            backgroundColor: "#ecfdf5",
                            borderRadius: "20px",
                            fontSize: "13px",
                            border: "1px solid #a7f3d0",
                          }}
                        >
                          <i
                            className="fa fa-map-marker"
                            style={{ color: "#10b981" }}
                          ></i>
                          <span style={{ fontWeight: "500", color: "#065f46" }}>{geo.name}</span>
                          <span style={{ color: "#059669", fontSize: "11px" }}>
                            ({geo.radiusMeters}m)
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div style={{ 
          display: "flex", 
          justifyContent: "flex-end", 
          marginTop: "24px",
          paddingTop: "20px",
          borderTop: "1px solid #e5e7eb"
        }}>
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedPartner}
            style={{
              ...styles.btnPrimary,
              backgroundColor: loading || !selectedPartner ? "#9ca3af" : "#10b981",
              cursor: loading || !selectedPartner ? "not-allowed" : "pointer",
              padding: "14px 32px",
              fontSize: "15px",
            }}
          >
            {loading ? (
              <>
                <i className="fa fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fa fa-check"></i>
                {isEdit ? "Update Configuration" : "Create Configuration"}
              </>
            )}
          </button>
        </div>

      {/* Shift Modal */}
      {showShiftModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowShiftModal(false)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "480px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", backgroundColor: "#fafbfc" }}>
              <h5 style={{ margin: 0, color: "#1f2937", fontSize: "18px", fontWeight: "600", display: "flex", alignItems: "center", gap: "10px" }}>
                <i className="fa fa-clock-o" style={{ color: "#f59e0b" }}></i>
                Create New Shift
              </h5>
            </div>
            
            <div style={{ padding: "24px" }}>
              <div style={{ marginBottom: "20px" }}>
                <label style={styles.label}>Shift Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Morning Shift"
                  value={newShift.name}
                  onChange={(e) =>
                    setNewShift({ ...newShift, name: e.target.value })
                  }
                  style={styles.input}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  marginBottom: "24px",
                }}
              >
                <div>
                  <label style={styles.label}>Start Time *</label>
                  <input
                    type="time"
                    value={newShift.startTime}
                    onChange={(e) =>
                      setNewShift({ ...newShift, startTime: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>
                <div>
                  <label style={styles.label}>End Time *</label>
                  <input
                    type="time"
                    value={newShift.endTime}
                    onChange={(e) =>
                      setNewShift({ ...newShift, endTime: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>
              </div>

              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
              >
                <button
                  onClick={() => setShowShiftModal(false)}
                  style={styles.btnSecondary}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateShift}
                  style={styles.btnPrimary}
                >
                  <i className="fa fa-plus"></i>
                  Create Shift
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Geofence Modal */}
      {showGeofenceModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowGeofenceModal(false)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "520px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", backgroundColor: "#fafbfc" }}>
              <h5 style={{ margin: 0, color: "#1f2937", fontSize: "18px", fontWeight: "600", display: "flex", alignItems: "center", gap: "10px" }}>
                <i className="fa fa-map-marker" style={{ color: "#ef4444" }}></i>
                Add New Location
              </h5>
            </div>

            <div style={{ padding: "24px", overflowY: "auto" }}>
              <div style={{ marginBottom: "20px" }}>
                <label style={styles.label}>Location Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Main Office, Warehouse"
                  value={newGeofence.name}
                  onChange={(e) =>
                    setNewGeofence({ ...newGeofence, name: e.target.value })
                  }
                  style={styles.input}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={styles.label}>Address</label>
                <input
                  type="text"
                  placeholder="Full address (optional)"
                  value={newGeofence.address}
                  onChange={(e) =>
                    setNewGeofence({ ...newGeofence, address: e.target.value })
                  }
                  style={styles.input}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <label style={styles.label}>Latitude *</label>
                  <input
                    type="number"
                    step="0.000001"
                    placeholder="e.g., 25.2048"
                    value={newGeofence.latitude}
                    onChange={(e) =>
                      setNewGeofence({ ...newGeofence, latitude: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>
                <div>
                  <label style={styles.label}>Longitude *</label>
                  <input
                    type="number"
                    step="0.000001"
                    placeholder="e.g., 55.2708"
                    value={newGeofence.longitude}
                    onChange={(e) =>
                      setNewGeofence({ ...newGeofence, longitude: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <button
                  onClick={getCurrentLocation}
                  style={{
                    backgroundColor: "#3b82f6",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    padding: "12px 18px",
                    cursor: "pointer",
                    width: "100%",
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <i className="fa fa-crosshairs"></i>
                  Use Current Location
                </button>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label style={styles.label}>Radius (meters)</label>
                <input
                  type="number"
                  min="10"
                  max="1000"
                  placeholder="Default: 100"
                  value={newGeofence.radiusMeters}
                  onChange={(e) =>
                    setNewGeofence({
                      ...newGeofence,
                      radiusMeters: e.target.value,
                    })
                  }
                  style={styles.input}
                />
                <p style={{ color: "#6b7280", marginTop: "8px", fontSize: "12px" }}>
                  Drivers must be within this radius to check in/out
                </p>
              </div>

              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
              >
                <button
                  onClick={() => setShowGeofenceModal(false)}
                  style={styles.btnSecondary}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGeofence}
                  style={styles.btnPrimary}
                >
                  <i className="fa fa-plus"></i>
                  Add Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
